import React from 'react';
import { Link } from 'react-router-dom';
import {
  ExternalLink,
  Sparkles,
  Heart,
  Layers,
  Award,
  ArrowRight,
  Dna,
  Rocket,
  Map,
} from 'lucide-react';
import SEO from '../components/SEO';

const capabilities = [
  { icon: Sparkles, title: 'AI Innovation', description: 'Cutting-edge artificial intelligence solutions that push the boundaries of what\'s possible.' },
  { icon: Heart, title: 'User-Centric Design', description: 'Interfaces designed with deep empathy for end users, backed by research and testing.' },
  { icon: Layers, title: 'Scalable Solutions', description: 'Architecture built to grow seamlessly from prototype to millions of users.' },
  { icon: Award, title: 'Industry Excellence', description: 'Rigorous quality standards that meet and exceed industry regulatory requirements.' },
];

const Portfolio = () => {
  return (
    <>
      <SEO
        title="Portfolio"
        description="Featured projects and case studies from P2P Digital — medical devices, AI platforms, and digital health applications."
        path="/portfolio"
      />
      <div>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[120px] -top-40 -right-40" />
        <div className="absolute w-[400px] h-[400px] bg-indigo-500/10 rounded-full blur-[120px] bottom-0 -left-40" />
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400">Our Work</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mt-4">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-indigo-400">Innovation</span> in Action
          </h1>
          <p className="mt-6 text-lg text-slate-400 max-w-2xl mx-auto">
            Real-world projects where we've transformed ambitious ideas into impactful technology solutions.
          </p>
        </div>
      </section>

      {/* Featured Project — Quokkacorn */}
      <section className="py-24 max-w-7xl mx-auto px-6">
        <div className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] rounded-2xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Video */}
            <div className="relative h-80 lg:h-auto bg-black flex items-center justify-center">
              <video
                className="w-full h-full object-cover"
                src="/onboarding-demo.mp4"
                autoPlay
                loop
                muted
                playsInline
                preload="none"
              />
            </div>

            {/* Content */}
            <div className="p-8 lg:p-12">
              <span className="inline-block px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-mono font-semibold uppercase tracking-wider">
                AI Venture Studio
              </span>
              <h2 className="font-display text-3xl font-bold text-white mt-4">Quokkacorn</h2>
              <p className="text-emerald-400 font-mono text-sm mt-1">Build Your AI Venture the Founder-First Way</p>
              <p className="text-slate-400 mt-4 leading-relaxed">
                A founder-first AI venture studio that helps entrepreneurs turn niche domain expertise into resilient,
                profitable AI agent businesses — without giving up control. Portfolio-led proof with real ventures in
                market, not just promises.
              </p>

              {/* Features */}
              <div className="mt-6 grid grid-cols-2 gap-3">
                {['AI Agent Ventures', 'Founder-First Model', 'Portfolio-Led Proof', 'First Dollar in 9 Days'].map((feature) => (
                  <div key={feature} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    <span className="text-slate-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Tech Stack */}
              <div className="mt-6 flex flex-wrap gap-2">
                {['Next.js', 'React', 'AI/ML', 'Tailwind CSS', 'Vercel'].map((tech) => (
                  <span
                    key={tech}
                    className="px-3 py-1 rounded-lg bg-white/[0.06] border border-white/[0.06] text-slate-300 text-xs font-mono"
                  >
                    {tech}
                  </span>
                ))}
              </div>

              {/* CTA */}
              <a
                href="https://www.quokkacorn.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-xl font-display font-semibold text-white text-sm transition-all duration-300 hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #10b981, #059669)', boxShadow: '0 0 30px rgba(16, 185, 129, 0.25)' }}
              >
                Visit Project <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Project — WA-i */}
      <section className="py-24 max-w-7xl mx-auto px-6">
        <div className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] rounded-2xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Content */}
            <div className="p-8 lg:p-12">
              <span className="inline-block px-3 py-1 rounded-full bg-amber-500/10 text-amber-400 text-xs font-mono font-semibold uppercase tracking-wider">
                AI Agency
              </span>
              <h2 className="font-display text-3xl font-bold text-white mt-4">WA-i</h2>
              <p className="text-amber-400 font-mono text-sm mt-1">AI Solutions That Take Your Business to the Next Level</p>
              <p className="text-slate-400 mt-4 leading-relaxed">
                A modern AI agency that designs, develops, and implements AI-powered tools and automations
                for startups and tech businesses. From workflow automation to custom AI assistants — helping
                businesses work smarter, not harder.
              </p>

              {/* Features */}
              <div className="mt-6 grid grid-cols-2 gap-3">
                {['Workflow Automation', 'AI Assistants', 'Sales & Marketing AI', 'Custom AI Projects'].map((feature) => (
                  <div key={feature} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                    <span className="text-slate-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Tech Stack */}
              <div className="mt-6 flex flex-wrap gap-2">
                {['Python', 'AI/ML', 'Framer', 'ElevenLabs', 'Automation'].map((tech) => (
                  <span
                    key={tech}
                    className="px-3 py-1 rounded-lg bg-white/[0.06] border border-white/[0.06] text-slate-300 text-xs font-mono"
                  >
                    {tech}
                  </span>
                ))}
              </div>

              {/* CTA */}
              <a
                href="https://www.wa-i.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-xl font-display font-semibold text-white text-sm transition-all duration-300 hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', boxShadow: '0 0 30px rgba(245, 158, 11, 0.25)' }}
              >
                Visit Project <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            {/* Video */}
            <div className="relative h-80 lg:h-auto bg-black flex items-center justify-center">
              <video
                className="w-full h-full object-cover"
                src="/wa-i-demo.mp4"
                autoPlay
                loop
                muted
                playsInline
                preload="none"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Project — MyBioBlueprint */}
      <section className="py-24 max-w-7xl mx-auto px-6">
        <div className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] rounded-2xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Content */}
            <div className="p-8 lg:p-12">
              <span className="inline-block px-3 py-1 rounded-full bg-indigo-500/10 text-indigo-400 text-xs font-mono font-semibold uppercase tracking-wider">
                AI / HealthTech
              </span>
              <h2 className="font-display text-3xl font-bold text-white mt-4">MyBioBlueprint</h2>
              <p className="text-indigo-400 font-mono text-sm mt-1">Secure DNA, Lab & Wellness Tracking Platform</p>
              <p className="text-slate-400 mt-4 leading-relaxed">
                A personalised health intelligence platform that lets users securely upload and track their DNA results,
                lab work, and wellness metrics — all in one place. AI-driven insights help users understand their
                biological data and make informed decisions about their health journey.
              </p>

              {/* Features */}
              <div className="mt-6 grid grid-cols-2 gap-3">
                {['DNA Analysis', 'Lab Tracking', 'AI Health Insights', 'Secure Data Vault'].map((feature) => (
                  <div key={feature} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                    <span className="text-slate-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Tech Stack */}
              <div className="mt-6 flex flex-wrap gap-2">
                {['React', 'Vite', 'AI/ML', 'Firebase', 'Tailwind CSS'].map((tech) => (
                  <span
                    key={tech}
                    className="px-3 py-1 rounded-lg bg-white/[0.06] border border-white/[0.06] text-slate-300 text-xs font-mono"
                  >
                    {tech}
                  </span>
                ))}
              </div>

              {/* CTA */}
              <a
                href="https://www.mybioblueprint.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-xl font-display font-semibold text-white text-sm transition-all duration-300 hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', boxShadow: '0 0 30px rgba(99, 102, 241, 0.25)' }}
              >
                Visit Project <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            {/* Video */}
            <div className="relative h-80 lg:h-auto bg-black flex items-center justify-center">
              <video
                className="w-full h-full object-cover"
                src="/DashboardShowcase.mp4"
                autoPlay
                loop
                muted
                playsInline
                preload="none"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Project — TreasureHuntr */}
      <section className="py-24 max-w-7xl mx-auto px-6">
        <div className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] rounded-2xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Video */}
            <div className="relative h-80 lg:h-auto bg-black flex items-center justify-center">
              <video
                className="w-full h-full object-cover"
                src="/TreasureHuntrPromo.mp4"
                autoPlay
                loop
                muted
                playsInline
                preload="none"
              />
            </div>

            {/* Content */}
            <div className="p-8 lg:p-12">
              <span className="inline-block px-3 py-1 rounded-full bg-rose-500/10 text-rose-400 text-xs font-mono font-semibold uppercase tracking-wider">
                SaaS / Tourism
              </span>
              <h2 className="font-display text-3xl font-bold text-white mt-4">TreasureHuntr</h2>
              <p className="text-rose-400 font-mono text-sm mt-1">Turn Visits Into Adventures</p>
              <p className="text-slate-400 mt-4 leading-relaxed">
                A B2B SaaS platform that lets attraction venues launch branded, interactive scavenger-hunt
                experiences for their guests. Guests join via QR code with no app download — operators get
                a live dashboard with session tracking, analytics, and revenue metrics.
              </p>

              {/* Features */}
              <div className="mt-6 grid grid-cols-2 gap-3">
                {['QR Code Entry', 'AI Route Drafting', 'Live Ops Dashboard', 'White-Label Branding'].map((feature) => (
                  <div key={feature} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                    <span className="text-slate-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Tech Stack */}
              <div className="mt-6 flex flex-wrap gap-2">
                {['React', 'Vite', 'Firebase', 'Stripe', 'Tailwind CSS'].map((tech) => (
                  <span
                    key={tech}
                    className="px-3 py-1 rounded-lg bg-white/[0.06] border border-white/[0.06] text-slate-300 text-xs font-mono"
                  >
                    {tech}
                  </span>
                ))}
              </div>

              {/* CTA */}
              <a
                href="https://treasurehuntr.app"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-xl font-display font-semibold text-white text-sm transition-all duration-300 hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #f43f5e, #e11d48)', boxShadow: '0 0 30px rgba(244, 63, 94, 0.25)' }}
              >
                Visit Project <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Project — Interest-Ed */}
      <section className="py-24 max-w-7xl mx-auto px-6">
        <div className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] rounded-2xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Content */}
            <div className="p-8 lg:p-12">
              <span className="inline-block px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-400 text-xs font-mono font-semibold uppercase tracking-wider">
                AI / EdTech
              </span>
              <h2 className="font-display text-3xl font-bold text-white mt-4">Interest-Ed</h2>
              <p className="text-cyan-400 font-mono text-sm mt-1">AI-Powered Learning Platform for Children with ADHD</p>
              <p className="text-slate-400 mt-4 leading-relaxed">
                An intelligent educational platform that personalizes learning pathways and enhances student engagement
                through AI-driven content recommendations and adaptive learning strategies designed specifically for
                children with ADHD.
              </p>

              {/* Features */}
              <div className="mt-6 grid grid-cols-2 gap-3">
                {['Personalized Learning', 'AI-Driven Insights', 'ADHD-Friendly UX', 'Gamified Progress'].map((feature) => (
                  <div key={feature} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                    <span className="text-slate-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Tech Stack */}
              <div className="mt-6 flex flex-wrap gap-2">
                {['React', 'Python', 'TensorFlow', 'Firebase', 'Tailwind CSS'].map((tech) => (
                  <span
                    key={tech}
                    className="px-3 py-1 rounded-lg bg-white/[0.06] border border-white/[0.06] text-slate-300 text-xs font-mono"
                  >
                    {tech}
                  </span>
                ))}
              </div>

              {/* CTA */}
              <a
                href="https://www.interest-ed.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-xl font-display font-semibold text-white text-sm transition-all duration-300 hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)', boxShadow: '0 0 30px rgba(14, 165, 233, 0.25)' }}
              >
                Visit Project <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            {/* Video */}
            <div className="relative h-80 lg:h-auto bg-black flex items-center justify-center">
              <video
                className="w-full h-full object-cover"
                src="/InterestEdDemo.mp4"
                autoPlay
                loop
                muted
                playsInline
                preload="none"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 bg-[#111827]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400">Capabilities</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mt-3">
              What We Bring to Every Project
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {capabilities.map((cap) => {
              const Icon = cap.icon;
              return (
                <div
                  key={cap.title}
                  className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.07] hover:shadow-[0_20px_60px_rgba(14,165,233,0.12)]"
                >
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h3 className="font-display text-lg font-bold text-white mb-2">{cap.title}</h3>
                  <p className="text-slate-400 text-sm">{cap.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 max-w-7xl mx-auto px-6">
        <div className="relative rounded-3xl overflow-hidden p-16 text-center bg-gradient-to-r from-cyan-600/20 via-indigo-600/20 to-cyan-600/20">
          <div className="absolute w-[300px] h-[300px] bg-cyan-500/10 rounded-full blur-[100px] -top-20 -right-20" />
          <div className="absolute w-[300px] h-[300px] bg-indigo-500/10 rounded-full blur-[100px] -bottom-20 -left-20" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Have a Project in Mind?
            </h2>
            <p className="mt-4 text-slate-300 text-lg max-w-xl mx-auto">
              Let's discuss how we can bring your vision to life with our expertise in AI, medical devices, and software engineering.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 mt-8 px-8 py-4 rounded-xl font-display font-semibold text-white text-sm transition-all duration-300 hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)', boxShadow: '0 0 30px rgba(14, 165, 233, 0.25)' }}
            >
              Let's Talk <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default Portfolio;
